import { atom } from "jotai";
import { decodeJwtPayload } from "../lib/jwt";

interface User {
  id: number;
  login: string;
  avatarUrl: string;
  // OrbitLens platform staff (運営). UI hint only — gates the feedback admin
  // surface. Authoritative authz is server-side (RequireStaff, DB-backed),
  // so a stale/forged flag here grants nothing.
  isStaff: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
}

const storedToken = localStorage.getItem("token");

// is_staff rides in the JWT claim, so it survives reloads (decoded by
// userFromToken) and is available immediately at login (decoded here from the
// access token, since the callback hash carries no user object).
function isStaffFromToken(token: string | null): boolean {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  return payload?.is_staff === true;
}

// Restore user from JWT payload on reload
function userFromToken(token: string | null): User | null {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.login) return null;
  const login = payload.login as string;
  return {
    id: (payload.user_id as number) ?? 0,
    login,
    avatarUrl: `https://github.com/${login}.png?size=64`,
    isStaff: payload.is_staff === true,
  };
}

export const authAtom = atom<AuthState>({
  isAuthenticated: !!storedToken,
  user: userFromToken(storedToken),
  token: storedToken,
});

export const loginAction = atom(null, (_get, set, response: { access_token: string; refresh_token: string; user: { id: number; login: string; avatar_url: string } }) => {
  localStorage.setItem("token", response.access_token);
  localStorage.setItem("refresh_token", response.refresh_token);
  set(authAtom, {
    isAuthenticated: true,
    user: {
      id: response.user.id,
      login: response.user.login,
      avatarUrl: response.user.avatar_url,
      isStaff: isStaffFromToken(response.access_token),
    },
    token: response.access_token,
  });
});

export const logoutAction = atom(null, (_get, set) => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  set(authAtom, { isAuthenticated: false, user: null, token: null });
});
